import { FC } from 'react';
import { Box, Text, Checkbox, TextButton } from '@wix/design-system';
import { Trans, useTranslation } from '@wix/fe-essentials-standalone';
import { StatusAlertFilledSmall } from '@wix/wix-ui-icons-common';
import { specificTemplateDetailsPageInTemplatesMarketplaceGenericClick } from '@wix/bi-logger-partners-data/v2';
import { CUSTOM_CODE_KB_LINK } from '@utils/constants';
import { useBi } from '@components/Hooks/useBi';
import s from './CustomCodeConsentNewContent.module.scss';

interface ConsentModalContentProps {
  onClick: () => void;
  isChecked: boolean;
  hasError?: boolean;
  isMobile?: boolean;
  privacyPolicyUrl?: string;
}

const CustomCodeConsentNewContent: FC<ConsentModalContentProps> = ({
  onClick,
  isChecked,
  hasError,
  isMobile,
  privacyPolicyUrl,
}) => {
  const { t } = useTranslation();
  const logger = useBi();

  return (
    <Box direction="vertical" className={isMobile ? s.mobileLayout : s.layout}>
      <Text size="small" weight="thin" listStyle="circle">
        <ul className={s.listSpacing}>
          <li>
            <Trans i18nKey="marketplace.template-redemption.consent-new-content-bullet-1">
              <Text size="small" weight="thin">
                By adding this app you agree to the Wix App Market.
                <TextButton
                  size="small"
                  as="a"
                  href={CUSTOM_CODE_KB_LINK}
                  target="_blank"
                  onClick={() => {
                    logger.report(
                      specificTemplateDetailsPageInTemplatesMarketplaceGenericClick(
                        {
                          click_type:
                            'velo / blocks consent modal - Learn more',
                        },
                      ),
                    );
                  }}
                >
                  learn more
                </TextButton>
                about which data you’re sharing.
              </Text>
            </Trans>
          </li>
          <li>
            <Text size="small" weight="thin">
              {t(
                'marketplace.template-redemption.consent-new-content-bullet-2.part-1',
              )}
            </Text>
            &nbsp;
            {privacyPolicyUrl && (
              <Trans i18nKey="marketplace.template-redemption.consent-new-content-bullet-2.part-2">
                <Text size="small" weight="thin">
                  Review creators’
                  <TextButton
                    size="small"
                    as="a"
                    href={privacyPolicyUrl}
                    target="_blank"
                    onClick={() => {
                      logger.report(
                        specificTemplateDetailsPageInTemplatesMarketplaceGenericClick(
                          {
                            click_type:
                              'velo / blocks consent modal - privacy policy',
                          },
                        ),
                      );
                    }}
                  >
                    privacy policy.
                  </TextButton>
                </Text>
              </Trans>
            )}
          </li>
        </ul>
      </Text>
      <Box display="inline-block">
        <Checkbox
          size="small"
          checked={isChecked}
          onChange={onClick}
          hasError={hasError}
        >
          {t(
            'marketplace.template-redemption.consent-new-content.checkbox-note',
          )}
          <Box display="inline-block" className={s.asterisk}>
            *
          </Box>
        </Checkbox>
        {hasError && <ErrorMessage />}
      </Box>
    </Box>
  );
};

export default CustomCodeConsentNewContent;

const ErrorMessage = () => {
  const { t } = useTranslation();
  return (
    <Box align="left" paddingTop="6px" dataHook="consent-terms-error">
      <StatusAlertFilledSmall color="red" />
      <Text skin="error" size="small" weight="thin">
        &nbsp;
        {t(
          'marketplace.template-page.custom-code-consent-modal.checkbox.acceptance-error',
        )}
      </Text>
    </Box>
  );
};
